import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000, // 30 second default timeout
});

// Faster client for non-critical requests (like column loading)
const fastApiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000, // 30 second timeout (increased from 5s for slow networks/databases)
});

const api = {
  // Dashboard
  getDashboardStats: async () => {
    const response = await apiClient.get("/dashboard/stats");
    return response.data;
  },

  // Tenant Management
  getTenants: async () => {
    const response = await apiClient.get("/tenants");
    return response.data;
  },

  getTenant: async (tenantId) => {
    const response = await apiClient.get(`/tenants/${tenantId}`);
    return response.data;
  },

  createTenant: async (data) => {
    const response = await apiClient.post("/tenants", data);
    return response.data;
  },

  updateTenant: async (tenantId, data) => {
    const response = await apiClient.put(`/tenants/${tenantId}`, data);
    return response.data;
  },

  deleteTenant: async (tenantId) => {
    const response = await apiClient.delete(`/tenants/${tenantId}`);
    return response.data;
  },

  addTenantUnit: async (tenantId, unit) => {
    const response = await apiClient.post(`/tenants/${tenantId}/units`, unit);
    return response.data;
  },

  removeTenantUnit: async (tenantId, unitId) => {
    const response = await apiClient.delete(`/tenants/${tenantId}/units/${unitId}`);
    return response.data;
  },

  // Unit Schema Management (multi-schema support)
  addUnitSchema: async (tenantId, unitId, schema) => {
    const response = await apiClient.post(`/tenants/${tenantId}/units/${unitId}/schemas`, schema);
    return response.data;
  },

  removeUnitSchema: async (tenantId, unitId, schemaName) => {
    const response = await apiClient.delete(`/tenants/${tenantId}/units/${unitId}/schemas/${schemaName}`);
    return response.data;
  },

  updateUnitSchema: async (tenantId, unitId, schemaName, schema) => {
    const response = await apiClient.put(`/tenants/${tenantId}/units/${unitId}/schemas/${schemaName}`, schema);
    return response.data;
  },

  getUnitSchemas: async (tenantId, unitId) => {
    const response = await apiClient.get(`/tenants/${tenantId}/units/${unitId}/schemas`);
    return response.data;
  },

  getTenantSchemas: async (tenantId) => {
    const response = await apiClient.get(`/tenants/${tenantId}/schemas`);
    return response.data;
  },

  // Oracle Connections
  getOracleConnections: async () => {
    const response = await apiClient.get("/connections/oracle");
    return response.data;
  },
  
  createOracleConnection: async (data) => {
    const response = await apiClient.post("/connections/oracle", data);
    return response.data;
  },
  
  testOracleConnection: async (id) => {
    const response = await apiClient.post(`/connections/oracle/${id}/test`);
    return response.data;
  },
  
  deleteOracleConnection: async (id) => {
    const response = await apiClient.delete(`/connections/oracle/${id}`);
    return response.data;
  },
  
  updateOracleConnection: async (id, data) => {
    const response = await apiClient.put(`/connections/oracle/${id}`, data);
    return response.data;
  },

  // BigQuery Connections
  getBigQueryConnections: async () => {
    const response = await apiClient.get("/connections/bigquery");
    return response.data;
  },
  
  createBigQueryConnection: async (data) => {
    const response = await apiClient.post("/connections/bigquery", data);
    return response.data;
  },
  
  testBigQueryConnection: async (id) => {
    const response = await apiClient.post(`/connections/bigquery/${id}/test`);
    return response.data;
  },
  
  deleteBigQueryConnection: async (id) => {
    const response = await apiClient.delete(`/connections/bigquery/${id}`);
    return response.data;
  },
  
  updateBigQueryConnection: async (id, data) => {
    const response = await apiClient.put(`/connections/bigquery/${id}`, data);
    return response.data;
  },

  // SQL Server Connections
  getSQLServerConnections: async () => {
    const response = await apiClient.get("/connections/sqlserver");
    return response.data;
  },
  
  createSQLServerConnection: async (data) => {
    const response = await apiClient.post("/connections/sqlserver", data);
    return response.data;
  },
  
  testSQLServerConnection: async (id) => {
    const response = await apiClient.post(`/connections/sqlserver/${id}/test`);
    return response.data;
  },
  
  deleteSQLServerConnection: async (id) => {
    const response = await apiClient.delete(`/connections/sqlserver/${id}`);
    return response.data;
  },
  
  updateSQLServerConnection: async (id, data) => {
    const response = await apiClient.put(`/connections/sqlserver/${id}`, data);
    return response.data;
  },

  getSQLServerSchemas: async (connectionId) => {
    const response = await apiClient.get(`/sqlserver/${connectionId}/schemas`);
    return response.data;
  },

  getSQLServerTables: async (connectionId, schema = null) => {
    const params = schema ? { schema } : {};
    const response = await apiClient.get(`/sqlserver/${connectionId}/tables`, { params });
    return response.data;
  },

  getSQLServerColumns: async (connectionId, schema, table) => {
    const response = await fastApiClient.get(`/sqlserver/${connectionId}/tables/${schema}/${table}/columns`);
    return response.data;
  },

  previewSQLServerTable: async (connectionId, schema, table, limit = 10) => {
    const response = await apiClient.get(`/sqlserver/${connectionId}/preview/${schema}/${table}`, { params: { limit } });
    return response.data;
  },

  // PostgreSQL Connections
  getPostgreSQLConnections: async () => {
    const response = await apiClient.get("/connections/postgresql");
    return response.data;
  },
  
  createPostgreSQLConnection: async (data) => {
    const response = await apiClient.post("/connections/postgresql", data);
    return response.data;
  },
  
  testPostgreSQLConnection: async (id) => {
    const response = await apiClient.post(`/connections/postgresql/${id}/test`);
    return response.data;
  },
  
  deletePostgreSQLConnection: async (id) => {
    const response = await apiClient.delete(`/connections/postgresql/${id}`);
    return response.data;
  },
  
  updatePostgreSQLConnection: async (id, data) => {
    const response = await apiClient.put(`/connections/postgresql/${id}`, data);
    return response.data;
  },

  getPostgreSQLSchemas: async (connectionId) => {
    const response = await apiClient.get(`/postgresql/${connectionId}/schemas`);
    return response.data;
  },

  getPostgreSQLTables: async (connectionId, schema = null) => {
    const params = schema ? { schema } : {};
    const response = await apiClient.get(`/postgresql/${connectionId}/tables`, { params });
    return response.data;
  },

  getPostgreSQLColumns: async (connectionId, schema, table) => {
    const response = await fastApiClient.get(`/postgresql/${connectionId}/tables/${schema}/${table}/columns`);
    return response.data;
  },

  previewPostgreSQLTable: async (connectionId, schema, table, limit = 10) => {
    const response = await apiClient.get(`/postgresql/${connectionId}/preview/${schema}/${table}`, { params: { limit } });
    return response.data;
  },

  // Oracle Schema Browser
  getOracleSchemas: async (connectionId) => {
    const response = await apiClient.get(`/oracle/${connectionId}/schemas`);
    return response.data;
  },
  
  getOracleTables: async (connectionId, schema = null, search = null, limit = 100) => {
    const params = { limit };
    if (schema) params.schema = schema;
    if (search) params.search = search;
    const response = await apiClient.get(`/oracle/${connectionId}/tables`, { params });
    return response.data;
  },
  
  getOracleColumns: async (connectionId, tableName, schema = null) => {
    const params = schema ? { schema } : {};
    const response = await fastApiClient.get(`/oracle/${connectionId}/tables/${tableName}/columns`, { params });
    return response.data;
  },
  
  previewOracleData: async (connectionId, tableName, limit = 10, schema = null) => {
    const params = { limit };
    if (schema) params.schema = schema;
    const response = await apiClient.get(`/oracle/${connectionId}/tables/${tableName}/preview`, { params });
    return response.data;
  },

  // BigQuery Preview
  getBigQueryTables: async (connectionId, layer) => {
    const response = await apiClient.get(`/bigquery/${connectionId}/tables/${layer}`);
    return response.data;
  },
  
  previewBigQueryData: async (connectionId, layer, tableName, limit = 10) => {
    const response = await apiClient.get(`/bigquery/${connectionId}/preview/${layer}/${tableName}`, { params: { limit } });
    return response.data;
  },

  previewBigQueryTable: async (connectionId, dataset, tableName, limit = 50) => {
    const response = await apiClient.get(`/bigquery/${connectionId}/preview/${dataset}/${tableName}`, { params: { limit } });
    return response.data;
  },

  listBigQueryTables: async (connectionId, dataset) => {
    const response = await apiClient.get(`/bigquery/${connectionId}/tables/${dataset}`);
    return response.data;
  },

  // Pipelines
  getPipelines: async () => {
    const response = await apiClient.get("/pipelines");
    return response.data;
  },
  
  getPipeline: async (id) => {
    const response = await apiClient.get(`/pipelines/${id}`);
    return response.data;
  },
  
  createPipeline: async (data) => {
    const response = await apiClient.post("/pipelines", data);
    return response.data;
  },

  updatePipeline: async (id, data) => {
    const response = await apiClient.put(`/pipelines/${id}`, data);
    return response.data;
  },
  
  deletePipeline: async (id) => {
    const response = await apiClient.delete(`/pipelines/${id}`);
    return response.data;
  },

  exportPipelineForLocal: async (id) => {
    const response = await apiClient.get(`/pipelines/${id}/export`);
    return response.data;
  },

  getPipelineExportHistory: async (id) => {
    const response = await apiClient.get(`/pipelines/${id}/exports`);
    return response.data;
  },

  exportPipelineSQL: async (id, tenantId = null, unitId = null) => {
    const params = {};
    if (tenantId) params.tenant_id = tenantId;
    if (unitId) params.unit_id = unitId;
    const response = await apiClient.get(`/pipelines/${id}/export-sql`, { params });
    return response.data;
  },

  exportAutomationSQL: async (id, sourceDatabase = "SourceDB", destDatabase = "DestDB", cdcMinutes = 1, silverGoldMinutes = 5) => {
    const response = await apiClient.get(`/pipelines/${id}/export-automation`, {
      params: {
        source_database: sourceDatabase,
        dest_database: destDatabase,
        cdc_schedule_minutes: cdcMinutes,
        silver_gold_schedule_minutes: silverGoldMinutes
      },
      responseType: 'text'
    });
    return response.data;
  },

  exportSemanticLayer: async (id, sourceDatabase = "", dialect = "sqlserver") => {
    const response = await apiClient.get(`/pipelines/${id}/semantic-layer`, {
      params: { source_database: sourceDatabase, dialect }
    });
    return response.data;
  },

  exportTextToSQLPackage: async (id, config = {}) => {
    const response = await apiClient.post(`/pipelines/${id}/export-text-to-sql`, config, {
      responseType: 'arraybuffer'
    });
    return response.data;
  },

  exportCRMTextToSQLPackage: async (id, config = {}) => {
    const response = await apiClient.post(`/pipelines/${id}/export-crm-text-to-sql`, config, {
      responseType: 'arraybuffer'
    });
    return response.data;
  },
  
  runPipeline: async (id) => {
    const response = await apiClient.post(`/pipelines/${id}/run`);
    return response.data;
  },

  // Run pipeline for a specific tenant (multi-tenant execution)
  runPipelineForTenant: async (pipelineId, tenantId, unitId = null) => {
    const response = await apiClient.post(`/pipelines/${pipelineId}/run`, {
      tenant_id: tenantId,
      unit_id: unitId
    });
    return response.data;
  },

  // Run pipeline with schema selection (multi-schema support)
  runPipelineWithSchema: async (pipelineId, tenantId, unitId, schemaName = null, runAllSchemas = false, dataStrategy = null) => {
    const response = await apiClient.post(`/pipelines/${pipelineId}/run`, {
      tenant_id: tenantId,
      unit_id: unitId,
      schema_name: schemaName,
      run_all_schemas: runAllSchemas,
      data_strategy: dataStrategy
    });
    return response.data;
  },

  // Schedules
  getSchedules: async () => {
    const response = await apiClient.get("/schedules");
    return response.data;
  },
  
  createSchedule: async (data) => {
    const response = await apiClient.post("/schedules", data);
    return response.data;
  },
  
  deleteSchedule: async (id) => {
    const response = await apiClient.delete(`/schedules/${id}`);
    return response.data;
  },
  
  toggleSchedule: async (id) => {
    const response = await apiClient.post(`/schedules/${id}/toggle`);
    return response.data;
  },

  // Executions
  getExecutions: async (pipelineId = null) => {
    const params = pipelineId ? { pipeline_id: pipelineId } : {};
    const response = await apiClient.get("/executions", { params });
    return response.data;
  },
  
  getExecution: async (id) => {
    const response = await apiClient.get(`/executions/${id}`);
    return response.data;
  },

  // CDC - Datastream
  getCdcStatus: async () => {
    const response = await apiClient.get("/cdc/status");
    return response.data;
  },

  getCdcMethods: async () => {
    const response = await apiClient.get("/cdc/methods");
    return response.data;
  },

  getCdcMergeStrategies: async () => {
    const response = await apiClient.get("/cdc/merge-strategies");
    return response.data;
  },

  // CDC Oracle Sources
  getCdcOracleSources: async () => {
    const response = await apiClient.get("/cdc/oracle-sources");
    return response.data;
  },

  getCdcOracleSource: async (id) => {
    const response = await apiClient.get(`/cdc/oracle-sources/${id}`);
    return response.data;
  },

  // Generic CDC Sources (supports Oracle, SQL Server, PostgreSQL)
  getCdcSources: async (sourceType = null) => {
    const params = sourceType ? { source_type: sourceType } : {};
    const response = await apiClient.get("/cdc/sources", { params });
    return response.data;
  },

  getCdcSource: async (id) => {
    const response = await apiClient.get(`/cdc/sources/${id}`);
    return response.data;
  },

  createCdcSource: async (data) => {
    const response = await apiClient.post("/cdc/sources", data);
    return response.data;
  },

  deleteCdcSource: async (id) => {
    const response = await apiClient.delete(`/cdc/sources/${id}`);
    return response.data;
  },

  getCdcMethodsBySource: async (sourceType) => {
    const response = await apiClient.get(`/cdc/methods-by-source/${sourceType}`);
    return response.data;
  },

  // Legacy Oracle CDC Sources (kept for backward compatibility)
  createCdcOracleSource: async (data) => {
    const response = await apiClient.post("/cdc/oracle-sources", data);
    return response.data;
  },

  deleteCdcOracleSource: async (id) => {
    const response = await apiClient.delete(`/cdc/oracle-sources/${id}`);
    return response.data;
  },

  detectCdcPrimaryKeys: async (sourceId) => {
    const response = await apiClient.post(`/cdc/oracle-sources/${sourceId}/detect-primary-keys`);
    return response.data;
  },

  // CDC Streams
  getCdcStreams: async () => {
    const response = await apiClient.get("/cdc/streams");
    return response.data;
  },

  getCdcStream: async (id) => {
    const response = await apiClient.get(`/cdc/streams/${id}`);
    return response.data;
  },

  createCdcStream: async (data) => {
    const response = await apiClient.post("/cdc/streams", data);
    return response.data;
  },

  startCdcStream: async (id) => {
    const response = await apiClient.post(`/cdc/streams/${id}/start`);
    return response.data;
  },

  pauseCdcStream: async (id) => {
    const response = await apiClient.post(`/cdc/streams/${id}/pause`);
    return response.data;
  },

  pollCdcNow: async (id) => {
    const response = await apiClient.post(`/cdc/streams/${id}/poll-now`);
    return response.data;
  },

  deleteCdcStream: async (id) => {
    const response = await apiClient.delete(`/cdc/streams/${id}`);
    return response.data;
  },

  // CDC Monitoring
  getCdcStreamMetrics: async (id) => {
    const response = await apiClient.get(`/cdc/streams/${id}/metrics`);
    return response.data;
  },

  getCdcStreamTables: async (id) => {
    const response = await apiClient.get(`/cdc/streams/${id}/tables`);
    return response.data;
  },

  // CDC Transformations
  getCdcTransformations: async (streamId = null) => {
    const params = streamId ? { stream_id: streamId } : {};
    const response = await apiClient.get("/cdc/transformations", { params });
    return response.data;
  },

  getCdcTransformation: async (id) => {
    const response = await apiClient.get(`/cdc/transformations/${id}`);
    return response.data;
  },

  createCdcTransformation: async (data) => {
    const response = await apiClient.post("/cdc/transformations", data);
    return response.data;
  },

  deleteCdcTransformation: async (id) => {
    const response = await apiClient.delete(`/cdc/transformations/${id}`);
    return response.data;
  },

  runCdcTransformation: async (id, tenantId = null, unitId = null) => {
    const params = {};
    if (tenantId) params.tenant_id = tenantId;
    if (unitId) params.unit_id = unitId;
    const response = await apiClient.post(`/cdc/transformations/${id}/run`, null, { params });
    return response.data;
  },

  getCdcDatasetTables: async (streamId) => {
    const response = await apiClient.get(`/cdc/datasets/${streamId}/tables`);
    return response.data;
  },

  getCdcTableSchema: async (connectionId, dataset, table) => {
    const response = await apiClient.get("/cdc/table-schema", {
      params: { bigquery_connection_id: connectionId, dataset, table }
    });
    return response.data;
  },

  // Row-Level Security (RLS)
  getRLSPolicies: async (tenantId = null, tableName = null) => {
    const params = {};
    if (tenantId) params.tenant_id = tenantId;
    if (tableName) params.table_name = tableName;
    const response = await apiClient.get("/rls/policies", { params });
    return response.data;
  },

  getRLSPolicy: async (policyId) => {
    const response = await apiClient.get(`/rls/policies/${policyId}`);
    return response.data;
  },

  createRLSPolicy: async (data, bigqueryConnectionId) => {
    const response = await apiClient.post("/rls/policies", data, {
      params: { bigquery_connection_id: bigqueryConnectionId }
    });
    return response.data;
  },

  applyRLSPolicy: async (policyId) => {
    const response = await apiClient.post(`/rls/policies/${policyId}/apply`);
    return response.data;
  },

  deleteRLSPolicy: async (policyId, removeFromBigquery = true) => {
    const response = await apiClient.delete(`/rls/policies/${policyId}`, {
      params: { remove_from_bigquery: removeFromBigquery }
    });
    return response.data;
  },

  getGoldTablesForRLS: async (bigqueryConnectionId) => {
    const response = await apiClient.get("/rls/gold-tables", {
      params: { bigquery_connection_id: bigqueryConnectionId }
    });
    return response.data;
  },

  getTableRLSPolicies: async (tableName, bigqueryConnectionId) => {
    const response = await apiClient.get(`/rls/table-policies/${tableName}`, {
      params: { bigquery_connection_id: bigqueryConnectionId }
    });
    return response.data;
  },

  // AI Agent
  getAIAgentUseCases: async () => {
    const response = await apiClient.get("/ai-agent/use-cases");
    return response.data;
  },

  getAIAgentAvailableTables: async () => {
    const response = await apiClient.get("/ai-agent/available-tables");
    return response.data;
  },

  sendAIAgentMessage: async (data) => {
    const response = await apiClient.post("/ai-agent/chat", data);
    return response.data;
  },

  clearAIAgentSession: async (sessionId) => {
    const response = await apiClient.delete(`/ai-agent/session/${sessionId}`);
    return response.data;
  },

  // Get Gold table columns from BigQuery
  getGoldTableColumns: async (connectionId, tableName) => {
    const response = await apiClient.get(`/bigquery/${connectionId}/gold/${tableName}/columns`);
    return response.data;
  },

  // Get existing Gold tables from BigQuery
  getBigQueryGoldTables: async (connectionId) => {
    const response = await apiClient.get(`/bigquery/${connectionId}/gold/tables`);
    return response.data;
  },

  // Parse SQL to Gold stages
  parseGoldSQL: async (data) => {
    const response = await apiClient.post("/ai-agent/parse-gold-sql", data, {
      timeout: 120000 // 2 minute timeout for parsing
    });
    return response.data;
  },

  // AI Gold Generation
  generateGoldStage: async (data) => {
    const response = await apiClient.post("/ai-agent/generate-gold", data, {
      timeout: 180000 // 180 second timeout for AI generation (complex multi-stage can take longer)
    });
    return response.data;
  },

  // Chatbot Publishing
  getChatbotPresets: async () => {
    const response = await apiClient.get("/chatbots/presets");
    return response.data;
  },

  createChatbot: async (data) => {
    const response = await apiClient.post("/chatbots", data);
    return response.data;
  },

  listChatbots: async () => {
    const response = await apiClient.get("/chatbots");
    return response.data;
  },

  getChatbot: async (id) => {
    const response = await apiClient.get(`/chatbots/${id}`);
    return response.data;
  },

  updateChatbot: async (id, data) => {
    const response = await apiClient.put(`/chatbots/${id}`, data);
    return response.data;
  },

  deleteChatbot: async (id) => {
    const response = await apiClient.delete(`/chatbots/${id}`);
    return response.data;
  },

  testChatbot: async (id, message, sessionId = null) => {
    const response = await apiClient.post(`/chatbots/${id}/test`, {
      message,
      session_id: sessionId
    });
    return response.data;
  },

  // Public chat endpoint (for external use)
  publicChat: async (endpointKey, message, sessionId = null) => {
    const response = await apiClient.post(`/chat/${endpointKey}`, {
      message,
      session_id: sessionId
    });
    return response.data;
  },
};

export default api;
